import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useBrand } from '../context/BrandContext'
import { useNavigate } from 'react-router-dom'
import { colors, radius, s, ui } from '../styles/theme'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000'

const style = {
  card: { ...s.card, padding: '24px', marginBottom: 0 },
  input: { ...s.input, padding: '10px', fontSize: '14px' },
  label: { ...s.labelNormal, marginBottom: '6px' },
  select: { ...s.select, padding: '10px', fontSize: '14px' },
  code: { background: colors.bgPage, border: `1px solid ${colors.border}`, padding: '2px 6px', borderRadius: radius.sm },
  hint: { fontSize: '11px', color: colors.textMuted },
  sectionHead: { ...s.sectionTitle, margin: '0 0 16px' },
  sectionBlock: { marginBottom: '20px', paddingBottom: '20px', borderBottom: `1px solid ${colors.borderLight}` }
}

const FREQ_OPTIONS = [
  { value: 'session', label: 'Her oturumda 1 kez' },
  { value: 'daily', label: 'Günde 1 kez' },
  { value: 'weekly', label: 'Haftada 1 kez' },
  { value: 'once', label: 'Sadece 1 kez (bir daha asla)' },
  { value: 'always', label: 'Her sayfada göster (sınırsız)' }
]

const TRIGGER_OPTIONS = [
  { value: 'immediate', label: 'Sayfa açılır açılmaz' },
  { value: 'delay', label: 'X saniye sonra' },
  { value: 'scroll', label: 'Sayfanın %X kadarı scroll edilince' }
]

const CLOSE_OPTIONS = [
  { value: 'minimize', label: 'Küçük tab olarak sağda kal' },
  { value: 'hide', label: 'Kapatınca tamamen gizle (o oturum için)' },
  { value: 'reopen', label: 'X dakika sonra tekrar aç' }
]

const defaultForm = {
  name: '', url_contains: '', title: 'Sana Özel Teklif', message: '', coupon_code: '',
  duration_minutes: 15, display_frequency: 'session', trigger_type: 'immediate',
  trigger_value: 0, after_close: 'minimize', reopen_minutes: 5, is_active: true,
  min_cart_total: null, max_cart_total: null, image_url: '', link_url: '', discount_label: '', exclusive: false, include_category_products: false,
  end_at: null, button_text: '', button_url: '', tab_text: '',
  url_list: [], form_enabled: false, form_recipients: '', form_kvkk_text: '', form_success_text: '',
  form_reply_enabled: false, form_reply_subject: '', form_reply_body: '',
  form_notify_subject: '', form_notify_title: '', form_notify_intro: '', form_bcc: ''
}

function urlListesiMetne(v) {
  if (!v) return ''
  if (Array.isArray(v)) return v.join('\n')
  if (typeof v === 'string') {
    const t = v.trim()
    if (t.charAt(0) === '[') { try { const j = JSON.parse(t); return Array.isArray(j) ? j.join('\n') : '' } catch (e) { return v } }
    return v
  }
  return ''
}

function urlListesiSay(v) {
  if (Array.isArray(v)) return v.length
  if (typeof v === 'string' && v.trim().charAt(0) === '[') { try { const j = JSON.parse(v); return Array.isArray(j) ? j.length : 0 } catch (e) { return 0 } }
  return 0
}

function toLocalInput(v) {
  if (!v) return ''
  const d = new Date(v)
  if (isNaN(d.getTime())) return ''
  const p = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

function fromLocalInput(v) {
  if (!v) return null
  const d = new Date(v)
  return isNaN(d.getTime()) ? null : d.toISOString()
}

function endLabel(v) {
  if (!v) return null
  const d = new Date(v)
  if (isNaN(d.getTime())) return null
  return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function SmartTarget() {
  const { selectedBrand } = useBrand()
  const navigate = useNavigate()
  const [targets, setTargets] = useState([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState('list')
  const [editTarget, setEditTarget] = useState(null)
  const [saved, setSaved] = useState(false)

  const token = localStorage.getItem('adminToken')
  const headers = { Authorization: token }

  useEffect(() => {
    if (!selectedBrand) return
    fetchTargets()
  }, [selectedBrand]) // eslint-disable-line

  function fetchTargets() {
    setLoading(true)
    axios.get(`${API}/admin/brands/${selectedBrand.id}/smart-targets`, { headers })
      .then(res => { setTargets(res.data); setLoading(false) })
      .catch(() => setLoading(false))
  }

  function showSaved() { setSaved(true); setTimeout(() => setSaved(false), 2500) }

  if (!selectedBrand) return (
    <div style={{ textAlign: 'center', padding: '60px' }}>
      <p style={{ ...ui.notice, display: 'inline-block', marginBottom: '16px' }}>Önce bir marka seçin.</p>
      <button onClick={() => navigate('/brands')} style={s.btnPrimary}>Markalara Git</button>
    </div>
  )

  if (view === 'form') {
    return <TargetForm brandId={selectedBrand.id} headers={headers} target={editTarget}
      onSave={() => { fetchTargets(); setView('list'); setEditTarget(null); showSaved() }}
      onCancel={() => { setView('list'); setEditTarget(null) }} />
  }

  if (view === 'records') {
    return <FormRecords headers={headers} target={editTarget}
      onBack={() => { setView('list'); setEditTarget(null) }} />
  }

  return (
    <div>
      <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
        <h2 style={{ ...s.pageTitle, minWidth: 0 }}>Smart Target — {selectedBrand.name}</h2>
        <button onClick={() => { setEditTarget(null); setView('form') }} style={{ ...s.btnPrimary, padding: '10px 20px' }}>
          + Yeni Kampanya
        </button>
      </div>

      {saved && <p style={{ ...ui.notice, display: 'inline-block', marginBottom: '16px' }}>Kaydedildi ✓</p>}

      {loading ? <p style={ui.emptyText}>Yükleniyor...</p> : targets.length === 0 ? (
        <div style={{ ...style.card, textAlign: 'center', padding: '48px', ...ui.emptyText }}>Henüz kampanya eklenmemiş.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {targets.map(t => (
            <div key={t.id} style={style.card}>
              <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '8px' }}>
                    <p className="sx-break" style={{ fontWeight: 600, fontSize: '15px', color: colors.textPrimary, letterSpacing: '-0.015em', margin: 0, minWidth: 0 }}>{t.name}</p>
                    <span style={ui.status(t.is_active)}><span style={ui.statusDot(t.is_active)} />{t.is_active ? 'Aktif' : 'Pasif'}</span>
                    {t.form_enabled && <span style={{ fontSize: '11px', fontWeight: 600, color: colors.textSecondary, background: colors.bgPage, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: '2px 8px' }}>Form</span>}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '13px', color: colors.textSecondary }}>
                    {t.url_contains && <span className="sx-break" style={{ minWidth: 0 }}>URL: <code style={style.code}>{t.url_contains}</code></span>}
                    {urlListesiSay(t.url_list) > 0 && <span style={ui.numMuted}>+{urlListesiSay(t.url_list)} URL</span>}
                    {t.coupon_code && <span className="sx-break" style={{ minWidth: 0 }}>Kupon: <code style={{ ...style.code, fontWeight: 600 }}>{t.coupon_code}</code></span>}
                    <span style={ui.numMuted}>{t.end_at ? `Bitiş: ${endLabel(t.end_at)}` : `${t.duration_minutes} dk`}</span>
                    <span>{FREQ_OPTIONS.find(f => f.value === t.display_frequency)?.label}</span>
                    <span>{TRIGGER_OPTIONS.find(f => f.value === t.trigger_type)?.label}{t.trigger_value > 0 ? ` (${t.trigger_value}${t.trigger_type === 'delay' ? 'sn' : '%'})` : ''}</span>
                  </div>
                  {t.message && <p className="sx-break" style={{ margin: '8px 0 0', fontSize: '13px', color: colors.textMuted, fontStyle: 'italic' }}>"{t.message}"</p>}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginLeft: '16px' }}>
                  {t.form_enabled && <button onClick={() => { setEditTarget(t); setView('records') }} style={ui.btnSm}>Form Kayıtları</button>}
                  <button onClick={() => { setEditTarget(t); setView('form') }} style={ui.btnSm}>Düzenle</button>
                  <button onClick={async () => {
                    if (!window.confirm('Bu kampanyayı silmek istediğinize emin misiniz?')) return
                    await axios.delete(`${API}/admin/smart-targets/${t.id}`, { headers })
                    fetchTargets()
                  }} style={ui.btnDangerQuiet}>Sil</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function FormRecords({ headers, target, onBack }) {
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    axios.get(`${API}/admin/smart-targets/${target.id}/forms`, { headers })
      .then(res => { setRows(res.data.rows || []); setTotal(res.data.total || 0); setLoading(false) })
      .catch(() => setLoading(false))
  }, []) // eslint-disable-line

  async function indir() {
    try {
      const res = await axios.get(`${API}/admin/smart-targets/${target.id}/forms/export`, { headers, responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `form-kayitlari-${target.id}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      alert('İndirilemedi: ' + (e.response?.data?.error || e.message))
    }
  }

  const th = { textAlign: 'left', padding: '10px 12px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: colors.textMuted, borderBottom: `1px solid ${colors.border}`, whiteSpace: 'nowrap' }
  const td = { padding: '11px 12px', fontSize: '13px', color: colors.textPrimary, borderBottom: `1px solid ${colors.borderLight}`, verticalAlign: 'top' }

  return (
    <div>
      <button onClick={onBack} style={{ ...ui.btnQuiet, marginBottom: '20px' }}>← Geri</button>
      <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ ...s.pageTitle, fontSize: '18px', margin: 0 }}>Form Kayıtları</h3>
          <p className="sx-break" style={{ margin: '4px 0 0', fontSize: '13px', color: colors.textSecondary }}>{target.name} — toplam {total} kayıt</p>
        </div>
        {rows.length > 0 && <button onClick={indir} style={{ ...s.btnSecondary, padding: '10px 20px' }}>CSV ile indir</button>}
      </div>

      {loading ? <p style={ui.emptyText}>Yükleniyor...</p> : rows.length === 0 ? (
        <div style={{ ...style.card, textAlign: 'center', padding: '48px', ...ui.emptyText }}>Henüz form gönderimi yok.</div>
      ) : (
        <div style={{ ...style.card, padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '960px' }}>
            <thead>
              <tr>
                <th style={th}>Tarih</th>
                <th style={th}>Ad Soyad</th>
                <th style={th}>Telefon</th>
                <th style={th}>E-posta</th>
                <th style={th}>KVKK</th>
                <th style={th}>IP</th>
                <th style={th}>Sayfa</th>
                <th style={th}>Bildirim</th>
                <th style={th}>Yanıt</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(k => (
                <tr key={k.id}>
                  <td style={{ ...td, ...ui.numMuted, whiteSpace: 'nowrap' }}>{k.created_at ? new Date(k.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{`${k.first_name || ''} ${k.last_name || ''}`.trim() || '-'}</td>
                  <td style={td}>{k.phone ? <a href={`tel:${String(k.phone).replace(/[^0-9+]/g, '')}`} style={{ color: colors.textPrimary, textDecoration: 'none' }}>{k.phone}</a> : '-'}</td>
                  <td style={td}>{k.email ? <a href={`mailto:${k.email}`} style={{ color: colors.textPrimary }}>{k.email}</a> : '-'}</td>
                  <td title={k.kvkk_text || ''} style={{ ...td, fontSize: '12px', color: k.kvkk ? colors.textSecondary : colors.dangerText, cursor: k.kvkk_text ? 'help' : 'default' }}>{k.kvkk ? 'Onaylı' : 'YOK'}</td>
                  <td style={{ ...td, ...ui.numMuted, fontSize: '12px', whiteSpace: 'nowrap' }}>{k.ip || '-'}</td>
                  <td style={{ ...td, maxWidth: '220px', wordBreak: 'break-all', fontSize: '12px', color: colors.textMuted }}>{k.page_url || '-'}</td>
                  <td style={td}>
                    {k.mail_sent
                      ? <span title={k.mail_error || ''} style={{ fontSize: '12px', color: k.mail_error ? colors.dangerText : colors.textSecondary, cursor: k.mail_error ? 'help' : 'default' }}>{k.mail_error ? 'Kısmi' : 'Gitti'}</span>
                      : <span title={k.mail_error || ''} style={{ fontSize: '12px', color: colors.dangerText, cursor: k.mail_error ? 'help' : 'default' }}>Gitmedi</span>}
                  </td>
                  <td style={td}>
                    {k.reply_sent
                      ? <span style={{ fontSize: '12px', color: colors.textSecondary }}>Gitti</span>
                      : <span title={k.reply_error || ''} style={{ fontSize: '12px', color: colors.textMuted, cursor: k.reply_error ? 'help' : 'default' }}>{k.reply_error ? 'Gitmedi' : '-'}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function TargetForm({ brandId, headers, target, onSave, onCancel }) {
  const [form, setForm] = useState(target ? { ...target } : { ...defaultForm })
  const [saving, setSaving] = useState(false)

  function set(field, value) { setForm(f => ({ ...f, [field]: value })) }

  async function handleSave() {
    if (!form.name) return alert('Kampanya adı zorunlu')
    if (form.form_enabled) {
      if (!String(form.form_recipients || '').trim()) return alert('Form için en az bir alıcı e-posta adresi zorunlu')
      if (!String(form.tab_text || '').trim()) return alert('Form için kulakçık metni zorunlu (ör. Demo Talep Formu)')
      if (form.form_reply_enabled && !String(form.form_reply_body || '').trim()) return alert('Otomatik yanıt açıkken yanıt metni zorunlu')
    } else {
      if (!form.image_url && !form.coupon_code) return alert('Kupon kodu zorunlu (veya bir görsel ekleyin)')
      if (form.image_url && !form.link_url) return alert('Görselli popup için tıklama linki (link URL) zorunlu')
    }
    setSaving(true)
    try {
      if (target) {
        await axios.put(`${API}/admin/smart-targets/${target.id}`, form, { headers })
      } else {
        await axios.post(`${API}/admin/brands/${brandId}/smart-targets`, form, { headers })
      }
      onSave()
    } catch (e) {
      alert('Hata: ' + (e.response?.data?.error || e.message))
    }
    setSaving(false)
  }

  return (
    <div>
      <button onClick={onCancel} style={{ ...ui.btnQuiet, marginBottom: '20px' }}>← Geri</button>
      <div style={style.card}>
        <h3 style={{ ...s.pageTitle, fontSize: '18px', margin: '0 0 24px' }}>{target ? 'Kampanyayı Düzenle' : 'Yeni Kampanya'}</h3>

        <div style={style.sectionBlock}>
          <h4 style={style.sectionHead}>Temel Bilgiler</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div><label style={style.label}>Kampanya Adı *</label><input style={style.input} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Çocuk Kitapları Kampanyası" /></div>
            <div><label style={style.label}>Başlık</label><input style={style.input} value={form.title} onChange={e => set('title', e.target.value)} /></div>
            <div><label style={style.label}>Kupon Kodu <span style={{ color: colors.textMuted, fontWeight: 400 }}>(görsel eklerseniz opsiyonel)</span></label><input style={style.input} value={form.coupon_code} onChange={e => set('coupon_code', e.target.value.toUpperCase())} /></div>
            <div><label style={style.label}>İndirim Rozeti <span style={{ color: colors.textMuted, fontWeight: 400 }}>(sepet kartında büyük gösterilir, ör. %10)</span></label><input style={style.input} value={form.discount_label} onChange={e => set('discount_label', e.target.value)} placeholder="%10" /></div>
            <div><label style={style.label}>URL Filtresi <span style={{ color: colors.textMuted, fontWeight: 400 }}>(boş = tüm sayfalar)</span></label><input style={style.input} value={form.url_contains} onChange={e => set('url_contains', e.target.value)} placeholder="/cocuk" /></div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={style.label}>Ek URL Listesi <span style={{ color: colors.textMuted, fontWeight: 400 }}>(her satıra bir URL — birden fazla ürün/kategori için)</span></label>
              <textarea style={{ ...style.input, minHeight: '92px', fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '12.5px', lineHeight: 1.6, resize: 'vertical' }}
                value={urlListesiMetne(form.url_list)}
                onChange={e => set('url_list', e.target.value)}
                placeholder={'/unox-speed-x-kombi-hizli-pisirme-firini-5-gn-2-3-elektrikli\n/kombi-firinlar\n/konveksiyonel-firinlar'} />
              <small style={style.hint}>Liste doluysa <b>yalnızca</b> bu URL'lerde (ve yukarıdaki URL Filtresi'nde) çalışır. Tam adres yapıştırabilirsiniz, otomatik olarak yol kısmına indirgenir. Boş bırakılırsa davranış değişmez.</small>
            </div>
            <div style={{ gridColumn: '1 / -1' }}><label style={style.label}>Mesaj / İçerik <span style={{ color: colors.textMuted, fontWeight: 400 }}>(opsiyonel — görselli popupta görselin üstündeki metin)</span></label><input style={style.input} value={form.message} onChange={e => set('message', e.target.value)} placeholder="Yeni çıkan ürünümüzü gördünüz mü?" /></div>
            <div style={{ gridColumn: '1 / -1', marginTop: '6px', paddingTop: '14px', borderTop: `1px dashed ${colors.border}` }}>
              <div style={{ fontSize: '12px', color: colors.textSecondary, lineHeight: 1.5 }}><b>Görselli Popup (opsiyonel):</b> Görsel URL girilirse popup, sağ-altta görselli kompakt kart olarak çıkar (kupon gerekmez). Görseli T-Soft FTP / medyaya yükleyip URL'sini yapıştırın. Sayaç istemezseniz aşağıdaki "Kampanya Süresi"ni 0 yapın.</div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}><label style={style.label}>Görsel URL <span style={{ color: colors.textMuted, fontWeight: 400 }}>(opsiyonel — PNG/JPG)</span></label><input style={style.input} value={form.image_url} onChange={e => set('image_url', e.target.value)} placeholder="https://satinal.timas.com.tr/Data/.../yeni-urun.png" /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={style.label}>Tıklama Linki <span style={{ color: colors.textMuted, fontWeight: 400 }}>(görsel varsa zorunlu)</span></label><input style={style.input} value={form.link_url} onChange={e => set('link_url', e.target.value)} placeholder="https://.../urun-sayfasi" /></div>
            {form.image_url ? <div style={{ gridColumn: '1 / -1' }}><label style={style.label}>Önizleme</label><div style={{ padding: '12px', background: colors.bgPage, border: `1px solid ${colors.border}`, borderRadius: radius.md, display: 'inline-block' }}><img src={form.image_url} alt="önizleme" style={{ maxWidth: '160px', maxHeight: '160px', display: 'block' }} onError={e => { e.target.style.display = 'none' }} /></div></div> : null}
            <div style={{ gridColumn: '1 / -1', marginTop: '6px', paddingTop: '14px', borderTop: `1px dashed ${colors.border}` }}>
              <div style={{ fontSize: '12px', color: colors.textSecondary, lineHeight: 1.5 }}><b>Yönlendirme Butonu (opsiyonel):</b> Hedef URL girilirse mesaj ile sayaç arasına buton eklenir. Boş bırakılırsa buton hiç basılmaz.</div>
            </div>
            <div><label style={style.label}>Buton Metni</label><input style={style.input} value={form.button_text || ''} onChange={e => set('button_text', e.target.value)} placeholder="Kitapları İncele" maxLength={80} /></div>
            <div><label style={style.label}>Buton Hedef URL</label><input style={style.input} value={form.button_url || ''} onChange={e => set('button_url', e.target.value)} placeholder="https://satinal.timas.com.tr/psikoloji-kitaplari" /></div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={style.label}>Kulakçık Metni <span style={{ color: colors.textMuted, fontWeight: 400 }}>(popup kapatılınca kenarda kalan sekme)</span></label>
              <input style={style.input} value={form.tab_text || ''} onChange={e => set('tab_text', e.target.value)} placeholder="%40 İndirim" maxLength={60} />
              <small style={style.hint}>Boş bırakılırsa eski davranış sürer: indirim rozeti varsa "&lt;rozet&gt; İndirim", yoksa "Sana Özel". Sekme dikey yazıldığı için kısa tutun.</small>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
            <div>
              <label style={style.label}>Min. Sepet Tutarı (TL)</label>
              <input style={style.input} type="number" placeholder="örn: 500"
                value={form.min_cart_total || ''}
                onChange={e => setForm(f => ({ ...f, min_cart_total: e.target.value || null }))} />
              <small style={style.hint}>Bu tutarın altında popup gösterilmez</small>
            </div>
            <div>
              <label style={style.label}>Max. Sepet Tutarı (TL)</label>
              <input style={style.input} type="number" placeholder="örn: 2000"
                value={form.max_cart_total || ''}
                onChange={e => setForm(f => ({ ...f, max_cart_total: e.target.value || null }))} />
              <small style={style.hint}>Bu tutarın üstünde popup gösterilmez</small>
            </div>
          </div>
        </div>

        <div style={style.sectionBlock}>
          <h4 style={style.sectionHead}>Form Toplama</h4>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px', marginBottom: form.form_enabled ? '16px' : 0, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!form.form_enabled} onChange={e => set('form_enabled', e.target.checked)} style={{ marginTop: '3px' }} />
            <span>Bu senaryo bir form olsun <span style={{ color: colors.textMuted, fontWeight: 400 }}>— popup/kupon yerine kenarda kulakçık çıkar, tıklayınca Ad, Soyad, Telefon, E-posta ve KVKK onayı alan bir form açılır. Gönderilen her form aşağıdaki adreslere mail olarak gider ve bu sayfada kayıt olarak listelenir.</span></span>
          </label>
          {form.form_enabled && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={style.label}>Alıcı E-posta(lar) *</label>
                <input style={style.input} value={form.form_recipients || ''} onChange={e => set('form_recipients', e.target.value)} placeholder="daghan.akdag@cafemarkt.com" />
                <small style={style.hint}>Birden fazla adres için virgülle ayırın. Bu alan ziyaretçiye hiç gönderilmez, sadece sunucuda tutulur.</small>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={style.label}>Gizli Kopya — BCC</label>
                <input style={style.input} value={form.form_bcc || ''} onChange={e => set('form_bcc', e.target.value)} placeholder="ornek@firma.com" />
                <small style={style.hint}>İsteğe bağlı. Buraya yazılan adresler bildirim mailinin bir kopyasını alır, ancak yukarıdaki alıcılar bu adresleri göremez. Birden fazla adres için virgülle ayırın. Alıcı alanı yine de zorunludur; burası tek başına mail göndermez.</small>
              </div>
              <div>
                <label style={style.label}>Bildirim Mailinin Konusu</label>
                <input style={style.input} value={form.form_notify_subject || ''} onChange={e => set('form_notify_subject', e.target.value)} placeholder="Cafemarkt UNOX Demo Talep Formu" maxLength={200} />
                <small style={style.hint}>Boş bırakılırsa "&lt;kulakçık metni&gt; — &lt;ad soyad&gt;" kullanılır.</small>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={style.label}>Bildirim Mailinin Giriş Cümlesi</label>
                <input style={style.input} value={form.form_notify_intro || ''} onChange={e => set('form_notify_intro', e.target.value)} placeholder="Cafemarkt.com üzerinden bir demo talebi alındı. Ürün ve müşteri detayları aşağıda yer almaktadır." maxLength={300} />
                <small style={style.hint}>Mailin en üstündeki açıklama satırı. Boş bırakılırsa "&lt;kulakçık metni&gt; senaryosu üzerinden yeni bir talep geldi."</small>
              </div>
              <div>
                <label style={style.label}>Bildirim Mailinin Başlığı</label>
                <input style={style.input} value={form.form_notify_title || ''} onChange={e => set('form_notify_title', e.target.value)} placeholder="Yeni Demo Talebi" maxLength={120} />
                <small style={style.hint}>Mailin mavi şeridinde yazan başlık. Boş bırakılırsa "Yeni Form Talebi".</small>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={style.label}>KVKK Onay Metni</label>
                <input style={style.input} value={form.form_kvkk_text || ''} onChange={e => set('form_kvkk_text', e.target.value)} placeholder="Kişisel verilerimin bu form kapsamında işlenmesine izin veriyorum." />
                <small style={style.hint}>Boş bırakılırsa varsayılan metin kullanılır. Bağlantı için <code>[metin](/adres)</code> yazın — altı çizili olarak yeni sekmede açılır.</small>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={style.label}>Gönderim Sonrası Mesaj</label>
                <input style={style.input} value={form.form_success_text || ''} onChange={e => set('form_success_text', e.target.value)} placeholder="Talebiniz bize ulaştı. En kısa sürede sizinle iletişime geçeceğiz." />
              </div>
              <div style={{ gridColumn: '1 / -1', marginTop: '4px', paddingTop: '14px', borderTop: `1px dashed ${colors.border}` }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.form_reply_enabled} onChange={e => set('form_reply_enabled', e.target.checked)} style={{ marginTop: '3px' }} />
                  <span>Formu dolduran kişiye otomatik yanıt gönder <span style={{ color: colors.textMuted, fontWeight: 400 }}>— markanın logosu, kurumsal künyesi ve yasal linkleriyle hazırlanmış bir mail olarak gider. Yalnızca e-posta girilmişse gönderilir; sadece telefon bırakan ziyaretçiye mail gitmez.</span></span>
                </label>
              </div>
              {form.form_reply_enabled && (
                <>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={style.label}>Yanıt Konusu</label>
                    <input style={style.input} value={form.form_reply_subject || ''} onChange={e => set('form_reply_subject', e.target.value)} placeholder="Cafemarkt UNOX Demo Talebiniz Hk." maxLength={200} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={style.label}>Yanıt Metni</label>
                    <textarea style={{ ...style.input, minHeight: '190px', lineHeight: 1.65, resize: 'vertical' }}
                      value={form.form_reply_body || ''}
                      onChange={e => set('form_reply_body', e.target.value)}
                      placeholder={'Merhaba,\n\nTalebiniz tarafımıza ulaşmıştır.\n\nEkibimiz en kısa sürede sizinle iletişime geçecektir.\n\nFirma Ekibi'} />
                    <small style={style.hint}>Paragrafları <b>boş satırla</b> ayırın. Başlık, logo, kurumsal künye ve yasal linkler otomatik eklenir — buraya yazmayın.</small>
                  </div>
                </>
              )}
              <div style={{ gridColumn: '1 / -1', fontSize: '12px', color: colors.textSecondary, lineHeight: 1.6, background: colors.bgPage, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '12px 14px' }}>
                Form modunda <b>Kulakçık Metni</b> zorunludur (kulakçıkta yazan metin), <b>Başlık</b> form penceresinin başlığı, <b>Mesaj / İçerik</b> ise formun üstündeki açıklama satırı olur. Kupon, görsel, sayaç ve buton alanları form modunda kullanılmaz. Hangi sayfalarda çıkacağını <b>Ek URL Listesi</b> ile belirleyin, <b>Gösterim Sıklığı</b>nı "Her sayfada göster" yapın.
              </div>
            </div>
          )}
        </div>

        <div style={style.sectionBlock}>
          <h4 style={style.sectionHead}>Zamanlama</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={style.label}>Kampanya Bitiş Tarihi <span style={{ color: colors.textMuted, fontWeight: 400 }}>(boş = kullanılmaz)</span></label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <input type="datetime-local" style={{ ...style.input, flex: '1 1 180px', minWidth: 0, maxWidth: '100%' }} value={toLocalInput(form.end_at)} onChange={e => set('end_at', fromLocalInput(e.target.value))} />
                {form.end_at && <button type="button" onClick={() => set('end_at', null)} style={{ ...ui.btnSm, padding: '0 12px' }}>Temizle</button>}
              </div>
              <small style={style.hint}>
                {form.end_at
                  ? 'Sayaç bu ana kadar saat:dakika:saniye olarak sayar, tarih geçince popup hiç gösterilmez.'
                  : 'Girilirse sayaç herkeste aynı ana kadar sayar ve süre alanı yok sayılır.'}
              </small>
            </div>
            <div><label style={style.label}>Gösterim Sıklığı</label><select style={style.select} value={form.display_frequency} onChange={e => set('display_frequency', e.target.value)}>{FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
            <div>
              <label style={style.label}>Kampanya Süresi (dakika) <span style={{ color: colors.textMuted, fontWeight: 400 }}>(0 = sayaç yok)</span></label>
              <input type="number" style={{ ...style.input, opacity: form.end_at ? 0.5 : 1 }} value={form.duration_minutes} onChange={e => set('duration_minutes', parseInt(e.target.value) || 0)} min="0" disabled={!!form.end_at} />
              <small style={style.hint}>Ziyaretçi popup'ı gördüğü andan itibaren sayar</small>
            </div>
          </div>
        </div>

        <div style={style.sectionBlock}>
          <h4 style={style.sectionHead}>Gösterim Tetikleyicisi</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div><label style={style.label}>Tetikleyici</label><select style={style.select} value={form.trigger_type} onChange={e => set('trigger_type', e.target.value)}>{TRIGGER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
            {form.trigger_type !== 'immediate' && (
              <div><label style={style.label}>{form.trigger_type === 'delay' ? 'Kaç saniye sonra?' : 'Scroll yüzdesi (%)'}</label><input type="number" style={style.input} value={form.trigger_value} onChange={e => set('trigger_value', parseInt(e.target.value))} min="1" /></div>
            )}
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h4 style={style.sectionHead}>Kapatma Sonrası Davranış</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div><label style={style.label}>Kapatınca ne olsun?</label><select style={style.select} value={form.after_close} onChange={e => set('after_close', e.target.value)}>{CLOSE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
            {form.after_close === 'reopen' && (
              <div><label style={style.label}>Kaç dakika sonra tekrar açılsın?</label><input type="number" style={style.input} value={form.reopen_minutes} onChange={e => set('reopen_minutes', parseInt(e.target.value))} min="1" /></div>
            )}
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', marginBottom: '14px', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
          <span>Kampanya aktif</span>
        </label>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px', marginBottom: '14px', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form.exclusive} onChange={e => set('exclusive', e.target.checked)} style={{ marginTop: '3px' }} />
          <span>Tekil gösterim <span style={{ color: colors.textMuted, fontWeight: 400 }}>— bu popup'ı gören kullanıcı, bu kampanya aktif olduğu sürece sitedeki başka hiçbir target popup'ını görmez (sepet dahil). Kullanıcı bu popup'ı görmediyse diğer popup'ları normal görmeye devam eder.</span></span>
        </label>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px', marginBottom: '24px', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form.include_category_products} onChange={e => set('include_category_products', e.target.checked)} style={{ marginTop: '3px' }} />
          <span>Kategori altı ürünlerde de göster <span style={{ color: colors.textMuted, fontWeight: 400 }}>— URL'deki kategorinin altındaki ürün sayfalarında da görünür (ürünün breadcrumb'ında bu kategori varsa). Yalnızca kategori sayfası için kapalı bırak.</span></span>
        </label>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <button onClick={handleSave} disabled={saving} style={{ ...s.btnPrimary, padding: '10px 24px', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
          <button onClick={onCancel} style={{ ...s.btnSecondary, padding: '10px 20px' }}>İptal</button>
        </div>
      </div>
    </div>
  )
}

export default SmartTarget